import { useEffect, useState } from "react";
import InstallmentsTable from "./Installment";
import {
  deleteMandateById,
  cancelMandateById,
  refreshMandateById,
} from "../../Services/Mandate/MandateService";
import { fetchProfile } from "../../Services/profile/profileservice";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { getInstallmentById } from "../../Services/Mandate/InstallmentService";
import {
  skipInstallmentById,
  refreshInstallmentById,
  retryInstallmentById,
} from "../../Services/Mandate/InstallmentService";
import Loader from "../../Loader";

const ViewMandateModal = ({
  open,
  data,
  loading,
  onClose,
  onSuccess,
  setGlobalLoading,
}) => {
  const [profile, setProfile] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [installmentOpen, setInstallmentOpen] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [mandateData, setMandateData] = useState(data);
  const instructions = [
    "Scan the QR code",
    "The link will open the gateway page",
    "Follow the steps to approve",
    'Click "Check Mandate Status" to confirm',
  ];
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [refreshPopupOpen, setRefreshPopupOpen] = useState(false);
  const [refreshedItem, setRefreshedItem] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const res = await fetchProfile();
        setProfile(res?.data);
      } catch (error) {
        console.error("Profile fetch failed", error);
      }
    };

    fetchProfileData();
  }, []);
  useEffect(() => {
    if (data) {
      setMandateData(data);
    }
  }, [data]);

  const mandateState = mandateData?.state ?? "";
  const mandateDeleted = mandateData?.deleted === true;
  const canDelete =
    (mandateState === "CREATED" ||
      mandateState === "ACCEPTED" ||
      mandateState === "ACTIVATED") &&
    !mandateDeleted;

  const disableInstallment =
    mandateState === "CREATED" ||
    mandateState === "ACCEPTED" ||
    mandateState === "ACTIVATED";
  const disableCancel =
    mandateState === "CREATED" ||
    mandateState === "ACCEPTED" ||
    mandateState === "ACTIVATED";
  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-GB") : "N/A";
  const formatText = (text) =>
    text
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  if (!open) return null;

  const buildPayload = () => {
    if (!profile || !data?.id) return null;

    return {
      api_id: profile?.user_id,
      api_key: profile?.api_key,
      token_id: profile?.token_id,
      mandate_id: data?.id,
    };
  };

  const buildPayloadInstallment = () => {
    if (!profile || !data?.id) return null;

    return {
      api_id: profile?.user_id,
      api_key: profile?.api_key,
      token_id: profile?.token_id,
    };
  };

  // -------------------- Mandate Details Handler --------------------
  const handleRefresh = async () => {
    const payload = buildPayload();
    if (!payload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    try {
      setViewLoading(true);
      const res = await refreshMandateById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(
          res?.data?.status?.message || "Failed to refresh mandate",
        );
        return;
      }
      setMandateData(res?.data);
      showSuccessToast("Mandate refreshed successfully");
    } catch (error) {
      showErrorToast(
        error?.response?.data?.status?.message || "Refresh failed",
      );
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async () => {
    const payload = buildPayload();
    if (!payload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    try {
      setViewLoading(true);
      const res = await deleteMandateById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(res?.data?.status?.message || "Delete Fail");
        return;
      }

      showSuccessToast("Mandate deleted successfully");
      setConfirmOpen(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorToast(error?.response?.data?.status?.message || "Delete failed");
    } finally {
      setViewLoading(false);
    }
  };

  const handleCancelMandate = async () => {
    const payload = buildPayload();
    if (!payload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    try {
      setViewLoading(true);
      const res = await cancelMandateById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(res?.data?.status?.message || "Cancel failed");
        return;
      }

      showSuccessToast("Mandate cancelled successfully");
      setConfirmOpen(false);
      onSuccess?.();
      onClose();
    } catch (error) {
      showErrorToast(error?.response?.data?.status?.message || "Cancel failed");
    } finally {
      setViewLoading(false);
    }
  };

  const handleInstallment = async () => {
    const payload = buildPayload();
    if (!payload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    try {
      setViewLoading(true);

      const res = await getInstallmentById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(
          res?.data?.status?.message || "Failed to fetch installments",
        );
        return;
      }

      setInstallments(res?.data?.items || []);
      setInstallmentOpen(true);
    } catch (error) {
      showErrorToast(
        error?.response?.data?.status?.message || "Installment fetch failed",
      );
    } finally {
      setViewLoading(false);
    }
  };

  //-------------------- Installment Handler------------------

  const handleRefreshInstallment = async (item) => {
    const basePayload = buildPayloadInstallment();
    if (!basePayload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    const payload = {
      ...basePayload,
      installment_id: item.id,
    };

    try {
      setViewLoading(true);
      const res = await refreshInstallmentById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(
          res?.data?.status?.message || "Failed to refresh installment",
        );
        return;
      }

      const updatedInstallment = res.data;

      setInstallments((prev) =>
        prev.map((i) =>
          i.id === updatedInstallment.id ? updatedInstallment : i,
        ),
      );
      setRefreshedItem(updatedInstallment);
      setRefreshPopupOpen(true);
      showSuccessToast("Installment refreshed");
    } catch (error) {
      showErrorToast(
        error?.response?.data?.status?.message || "Installment refresh failed",
      );
    } finally {
      setViewLoading(false);
    }
  };

  const handleRetryInstallment = async (item, retryDate) => {
    const basePayload = buildPayloadInstallment();
    if (!basePayload) {
      showErrorToast("Profile not loaded yet");
      return;
    }
    const payload = {
      ...basePayload,
      installment_id: item.id,
      schedule_date: retryDate,
    };

    try {
      setViewLoading(true);

      const res = await retryInstallmentById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        const currentStatus =
          res?.error?.current_status ||
          res?.data?.error?.current_status ||
          res?.data?.data?.error?.current_status ||
          "";

        const message =
          res?.data?.error?.message ||
          res?.data?.status?.message ||
          "Failed to Retry";

        showErrorToast(`${message} ${currentStatus}`.trim());

        return;
      }

      const updatedInstallment = res.data;

      setInstallments((prev) =>
        prev.map((i) =>
          i.id === updatedInstallment.id ? updatedInstallment : i,
        ),
      );

      showSuccessToast("Installment retry scheduled");
    } catch (error) {
      showErrorToast(error?.response?.data?.status?.message || "Retry failed");
    } finally {
      setViewLoading(false);
    }
  };

  const handleSkipInstallment = async (item) => {
    const basePayload = buildPayloadInstallment();
    if (!basePayload) {
      showErrorToast("Profile not loaded yet");
      return;
    }

    const payload = {
      ...basePayload,
      installment_id: item.id,
    };

    try {
      setViewLoading(true);

      const res = await skipInstallmentById(payload);

      if (res?.data?.status?.code !== 200 && res?.status?.code !== 200) {
        showErrorToast(res?.data?.status?.message || "Failed to skip");
        return;
      }

      const updatedInstallment = res.data;

      setInstallments((prev) =>
        prev.map((i) =>
          i.id === updatedInstallment.id ? updatedInstallment : i,
        ),
      );
      showSuccessToast("Installment refreshed");
    } catch (error) {
      showErrorToast(error?.response?.data?.status?.message || "Skip failed");
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <>
      <style>{rowStyles}</style>
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-2">
        <div className="bg-white w-full max-w-4xl rounded-xl shadow-lg flex flex-col max-h-[95vh]">
          <div className="relative px-5 py-2 flex items-center">
            <h2 className="absolute left-1/2 -translate-x-1/2 text-lg md:text-2xl font-semibold">
              Mandate Details
            </h2>

            <button
              onClick={onClose}
              className="ml-auto text-gray-500 hover:text-black text-xl"
            >
              ✕
            </button>
          </div>
          <div>
            <p className="text-sm md:text-md text-center text-gray-600">
              In case of any issue, please click the{" "}
              <span className="font-medium">Refresh</span> button to update the
              data.
            </p>
          </div>

          <div className="px-4 py-2 flex flex-wrap gap-2 justify-center sticky bottom-0 bg-white">
            <ActionButton
              label="Refresh"
              onClick={handleRefresh}
              disabled={viewLoading}
            />

            <ActionButton
              label="Installments"
              color="green"
              onClick={handleInstallment}
              disabled={!disableInstallment || viewLoading}
            />

            <ActionButton
              label="Cancel"
              color="yellow"
              disabled={!disableCancel || viewLoading}
              onClick={() => {
                setConfirmType("cancel");
                setConfirmOpen(true);
              }}
            />

            <ActionButton
              label="Delete"
              color="red"
              disabled={!canDelete || viewLoading }
              onClick={() => {
                setConfirmType("delete");
                setConfirmOpen(true);
              }}
            />
          </div>

          <div className="mx-4 mb-4 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-gray-700">
            <p className="font-semibold text-center text-md mb-1">
              Mandate Details Instructions
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Actions */}
              <div>
                <p className="font-medium mb-1">Actions</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <strong>Refresh</strong> – Reload or Refresh mandate details
                  </li>
                  <li>
                    <strong>Installments</strong> – View debit installment list
                  </li>
                  <li>
                    <strong>Cancel</strong> – Cancel the mandate permanently
                  </li>
                </ul>
              </div>

              {/* Important Notes */}
              <div>
                <p className="font-medium mb-1">Important Notes</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Cancelled mandates <strong>cannot be resumed</strong>
                  </li>
                  <li>
                    Only <strong>CREATED or ACCEPTED</strong> mandates can be{" "}
                    <strong>deleted</strong>
                  </li>
                  <li>
                    Click <strong>Refresh</strong> after customer approve the
                    mandate
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div className="pl-4 pr-4 pb-4 pt-4 overflow-y-auto border-t-2 space-y-4 text-sm">
            {loading && <p>Loading...</p>}

            {mandateData && (
              <>
                {(mandateData?.state === "CREATED" ||
                  mandateData?.state === "PAUSED") && !mandateDeleted && (
                  <Section title="Approve Mandate">
                    <div className="grid md:grid-cols-2 gap-6 items-start">
                      {/* LEFT SIDE */}
                      <div>
                        <p className="text-xl font-semibold text-gray-800 mb-2">
                          Instructions
                        </p>

                        <ol className="list-decimal list-inside space-y-2 text-gray-700 text-md">
                          {instructions.map((i, idx) => (
                            <li key={idx}>{i}</li>
                          ))}
                        </ol>

                        {data?.meta?.mandate_auth_checkout_url && (
                          <a
                            href={data.meta.mandate_auth_checkout_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-block text-blue-600 hover:underline text-md font-semibold"
                          >
                            Activate Mandate
                          </a>
                        )}
                      </div>

                      {/* RIGHT SIDE (QR) */}
                      {data?.meta?.mandate_auth_checkout_url && (
                        <div className="flex justify-end pt-2">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                              data.meta.mandate_auth_checkout_url,
                            )}`}
                            alt="QR"
                          />
                        </div>
                      )}
                    </div>
                  </Section>
                )}
                
                <Section title="Basic Information">
                  <Row label="Mandate ID" value={mandateData?.id} />
                  <Row label="Reference ID" value={mandateData?.reference_id} />
                  <Row
                    label="Reference Type"
                    value={mandateData?.reference_type || "N/A"}
                  />
                  <Row label="State" value={formatText(mandateData?.state)} />
                  <Row label="Frequency" value={mandateData?.frequency} />
                  <Row
                    label="Installments"
                    value={mandateData?.installment_count}
                  />
                  <Row
                    label="Start Date"
                    value={formatDate(mandateData?.start_date)}
                  />
                  <Row
                    label="End Date"
                    value={formatDate(mandateData?.end_date)}
                  />

                  <Row
                    label="Deleted"
                    value={mandateData?.deleted ? "Yes" : "No"}
                  />
                </Section>
                <Section title="Payer">
                  <Row
                    label="Name"
                    value={mandateData?.payer?.account?.name || "N/A"}
                  />

                  <Row
                    label="Mobile"
                    value={mandateData?.payer?.account?.mobile_number || "N/A"}
                  />

                  <Row
                    label="Account ID"
                    value={mandateData?.payer?.account_id || "N/A"}
                  />

                  <Row
                    label="Instrument ID"
                    value={mandateData?.payer?.instrument_id || "N/A"}
                  />
                  <Row
                    label="Mode"
                    value={
                      mandateData?.payer?.mode
                        ? formatText(mandateData.payer.mode)
                        : "N/A"
                    }
                  />

                  <Row
                    label="Tag"
                    value={
                      mandateData?.payer?.tag
                        ? formatText(mandateData?.payer?.tag)
                        : "N/A"
                    }
                  />

                  <Row
                    label="Amount"
                    value={
                      mandateData?.payer?.amount
                        ? `₹ ${Number(
                            mandateData.payer.amount.value,
                          ).toLocaleString("en-IN", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}`
                        : "N/A"
                    }
                  />
                </Section>
                <Section title="Instrument Details">
                  <Row
                    label="Account Holder (Bank)"
                    value={
                      mandateData?.payer?.instrument
                        ?.account_holder_name_at_bank || "N/A"
                    }
                  />
                  <Row
                    label="Account Holder Name"
                    value={
                      mandateData?.payer?.instrument?.account_holder_name ||
                      "N/A"
                    }
                  />
                  <Row
                    label="VPA"
                    value={mandateData?.payer?.instrument?.vpa || "N/A"}
                  />
                </Section>
                {Array.isArray(mandateData?.payees) &&
                  mandateData.payees.length > 0 && (
                    <Section title="Payees">
                      <div className="space-y-4">
                        {mandateData.payees.map((payee, index) => (
                          <div
                            key={index}
                            className="border rounded-lg p-3 bg-white"
                          >
                            <h4 className="font-semibold text-center text-md text-gray-700 mb-2">
                              Payee {index + 1}
                            </h4>

                            <Row
                              label="Tag"
                              value={formatText(payee?.tag) || "N/A"}
                            />

                            <Row label="Mode" value={payee?.mode || "N/A"} />

                            <Row
                              label="Amount"
                              value={
                                payee?.amount
                                  ? `₹ ${payee?.amount?.value}`
                                  : "N/A"
                              }
                            />

                            <Row
                              label="Account ID"
                              value={payee?.account_id || "N/A"}
                            />

                            <Row
                              label="Instrument ID"
                              value={payee?.instrument_id || "N/A"}
                            />

                            <Row
                              label="Account Name"
                              value={payee?.account?.name || "N/A"}
                            />

                            <Row
                              label="Mobile Number"
                              value={payee?.account?.mobile_number || "N/A"}
                            />
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                <Section title="Amounts">
                  <Row
                    label="Approval Amount"
                    value={`₹${mandateData?.approval_amount}`}
                  />
                  <Row
                    label="Advance Amount"
                    value={`₹${mandateData?.advance_amount}`}
                  />
                </Section>
                <Section title="Mandate Configuration">
                  <Row
                    label="Frequency"
                    value={mandateData?.frequency || "N/A"}
                  />

                  <Row
                    label="Installment Count"
                    value={
                      mandateData?.installment_count != null
                        ? mandateData?.installment_count
                        : "N/A"
                    }
                  />

                  <Row
                    label="Time Zone"
                    value={mandateData?.time_zone || "N/A"}
                  />
                </Section>
              </>
            )}
          </div>
        </div>

        <InstallmentsTable
          isOpen={installmentOpen}
          onClose={() => setInstallmentOpen(false)}
          items={installments}
          onRefresh={handleRefreshInstallment}
          onRetry={handleRetryInstallment}
          onSkip={handleSkipInstallment}
          refreshPopupOpen={refreshPopupOpen}
          refreshedItem={refreshedItem}
          onCloseRefreshPopup={() => setRefreshPopupOpen(false)}
        />

        <ConfirmModal
          open={confirmOpen}
          title={confirmType === "delete" ? "Delete Mandate" : "Cancel Mandate"}
          message={
            confirmType === "delete"
              ? "Are you sure you want to delete this mandate? This action cannot be undone."
              : "Are you sure you want to cancel this mandate?"
          }
          onCancel={() => setConfirmOpen(false)}
          onConfirm={
            confirmType === "delete" ? handleDelete : handleCancelMandate
          }
          loading={viewLoading}
        />
        {viewLoading && <Loader />}
      </div>
    </>
  );
};

export default ViewMandateModal;

const Section = ({ title, children }) => (
  <div className="bg-gray-50 rounded-lg pt-2 p-4 border-2 border-gray-400">
    <h3 className="font-semibold mb-1 text-md md:text-xl bg-blue-700 text-white text-center text-gray-800">
      {title}
    </h3>
    <div className="space-y-2 text-sm md:text-md">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="row border-b-2 p-1 border-gray-400">
    <span className="row-label text-gray-500">{label}</span>
    <span className="row-value font-medium">{value ?? "—"}</span>
  </div>
);

const ActionButton = ({ label, onClick, color = "blue", disabled = false }) => {
  const colors = {
    blue: "bg-blue-600 hover:bg-blue-700",
    red: "bg-red-600 hover:bg-red-700",
    green: "bg-green-600 hover:bg-green-700",
    yellow: "bg-yellow-500 hover:bg-yellow-600",
  };

  return (
    <button
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      className={`px-3 py-1 rounded-md text-white
                ${disabled ? "bg-gray-300 cursor-not-allowed" : colors[color]}`}
    >
      {label}
    </button>
  );
};

const ConfirmModal = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  loading,
}) => {
  if (!open) return null;

  const handleConfirm = () => {
    if (loading) return; // ⛔ prevent double click
    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-5">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-5">{message}</p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? "Processing..." : "Yes, Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const rowStyles = `
  .row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: flex-start;
  }

  .row-label {
    min-width: 140px;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
  }

  .row-value {
    flex: 1;
    text-align: right;
    word-break: break-word;
    overflow-wrap: anywhere;
    font-weight: 500;
  }

  /* Mobile only */
  @media (max-width: 640px) {
    .row {
      flex-direction: column;
      gap: 2px;
    }

    .row-label {
      white-space: normal;
    }

    .row-value {
      text-align: left;
    }
  }
`;
